
import { useCallback } from 'react';
import { TrainingSession, TrainingFormValues } from '@/features/training-journal/types';
import { TrainingForm } from '../training-form';
import { toast } from 'sonner';
import { useTrainingJournal } from '../../context/useTrainingJournal';
import { getFormValuesFromSession } from '../../utils/formUtils';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';

export interface SessionEditFormProps {
  session: TrainingSession | null;
  isOpen: boolean;
  onOpenChange: (open: boolean) => void;
  onSuccess?: () => void;
}

export function SessionEditForm({ 
  session, 
  isOpen, 
  onOpenChange,
  onSuccess 
}: SessionEditFormProps) {
  const { editSession } = useTrainingJournal();
  
  const handleSubmit = useCallback(async (formValues: TrainingFormValues) => {
    if (session && editSession) {
      try {
        // Call the edit session function and ensure we wait for it to complete
        await editSession(session.id, formValues);
        toast.success('Session updated successfully');
        
        // Force the dialog to close immediately
        onOpenChange(false);
        if (onSuccess) onSuccess();
      } catch (error) {
        console.error('Error updating session:', error);
        toast.error('Failed to update session');
      }
    }
  }, [session, editSession, onOpenChange, onSuccess]);

  const handleCancel = useCallback(() => {
    onOpenChange(false);
  }, [onOpenChange]);

  // Don't render anything if there's no session
  if (!session) {
    return null;
  }

  // Convert the session to form values (with date as a Date object)
  const initialValues = getFormValuesFromSession(session);

  return (
    <Dialog open={isOpen} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Edit Training Session</DialogTitle>
        </DialogHeader>
        <TrainingForm
          initialValues={initialValues || undefined}
          isEditing={true}
          sessionId={session.id}
          onSubmit={handleSubmit}
          onCancel={handleCancel}
        />
      </DialogContent>
    </Dialog>
  );
}
